"use client"

import React, {HTMLAttributes, useEffect, useState, useSyncExternalStore} from "react";
import Image from "next/image";
import {useTranslations} from "next-intl";
import {cn} from "@/lib/utils";
import {Link} from "@/i18n/navigation";
import AnjImage from "@/app/components/common/anj-image/AnjImage";
import {AppPromoCard} from "@/app/components/common/app-footer/AppPromoCard";
import {CasinoLinksCard, RulesCard, SocialCard, SupportCard} from "@/app/components/common/app-footer/FooterCards";

const RESTRICTED_DOMAINS = [
    'alddev.xyz',
    'www.alddev.xyz',
];

const SUPPORT_EMAIL = 'support@aldex.com';

// The copyright year, hydration-safe. React renders the UTC year on the server
// and during hydration, then swaps in the visitor's local year. Reading the
// local year during render mismatches for UTC-behind timezones in the hours
// after New Year, because Vercel runs UTC (AC-655).
const subscribeToYear = () => () => {
};
const getLocalYear = () => new Date().getFullYear();
const getUtcYear = () => new Date().getUTCFullYear();

// V2 footer (Figma "Footer / Premium"). The layout follows the footer's own width
// through container queries, not the viewport, because the desktop sidebar eats
// into it: @min-[640px] is the tablet layout (736 in Figma), @min-[1100px] the
// desktop one (1188). Below 640 is the compact phone footer (358).
export const AppFooter = ({className}: HTMLAttributes<HTMLDivElement>) => {
    const [showLicense, setShowLicense] = useState(false);
    const year = useSyncExternalStore(subscribeToYear, getLocalYear, getUtcYear);
    const t = useTranslations();

    useEffect(() => {
        const allowed = !RESTRICTED_DOMAINS.includes(window.location.hostname);
        setTimeout(() => {
            setShowLicense(allowed);
        }, 100)
    }, []);

    // pb-[88px] lets the page scroll the legal row / support email clear of the fixed
    // scroll-to-top button (desktop: 20px offset + 51px height; mobile: sits 148px up,
    // above the 104px nav clearance MobileVersion already adds).
    return (
        <footer className={cn('@container bg-dark-gray pt-[24px] pb-[88px] @min-[1100px]:pt-[64px]', className)}>
            <div className={'flex flex-col gap-[16px] @min-[1100px]:gap-[24px]'}>
                <div className={'flex items-center justify-between h-[32px]'}>
                    <div className={'flex items-center gap-[20px]'}>
                        <Link prefetch={false} href={'/'}>
                            <Image width={144} height={17} className={'w-[144px] h-[17px] active:scale-90 transition-transform duration-300'}
                                   src={'/icons/logo.svg'} alt={'Aldex'}/>
                        </Link>
                        <span className={'hidden @min-[1100px]:inline text-text-muted font-medium uppercase text-[11px] leading-[15px] tracking-[2.2px]'}>
                            {t('AppFooter.brandLine')}
                        </span>
                    </div>
                </div>

                <div className={'grid gap-[16px] @min-[1100px]:grid-cols-[minmax(0,800fr)_minmax(0,372fr)]'}>
                    <AppPromoCard/>
                    <div className={'grid gap-[16px] @min-[640px]:grid-cols-2 @min-[1100px]:flex @min-[1100px]:flex-col'}>
                        <SupportCard/>
                        <SocialCard/>
                    </div>
                </div>

                <div className={'grid gap-[16px] @min-[640px]:grid-cols-2 @min-[1100px]:grid-cols-[minmax(0,800fr)_minmax(0,372fr)]'}>
                    <CasinoLinksCard/>
                    <RulesCard/>
                </div>

                <p className={'text-text-subtle text-[11px] leading-[17px]'}>
                    {t.rich('AppFooter.description', {
                        site: (chunks) => <Link prefetch={false} href={'/'}>{chunks}</Link>
                    })}
                </p>

                <div className={cn(
                    'flex flex-col gap-[12px] border-t border-card-border pt-[19px] text-text-subtle text-[11px] leading-[15px]',
                    '@min-[640px]:flex-row @min-[640px]:items-center @min-[640px]:justify-between',
                )}>
                    <p>© {year} ALDEX.COM · {t('AppFooter.allRightsReserved')}</p>
                    <a href={`mailto:${SUPPORT_EMAIL}`} className={'text-[#BFBDB5] transition-colors hover:text-text-strong'}>
                        {SUPPORT_EMAIL}
                    </a>
                    <div className={'flex items-center gap-[12px]'}>
                        <span>{t('AppFooter.playResponsibly')}</span>
                        <span className={'flex items-center justify-center h-[28px] w-[46px] rounded-[14px] border border-[#55524A] text-text-strong font-semibold text-[13px] leading-[18px]'}>
                            18+
                        </span>
                        {showLicense ? <AnjImage/> : null}
                    </div>
                </div>
            </div>
        </footer>
    )
}
